// SuperAdminLayout — Premium dark sidebar + light content area
import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import {
  LayoutGrid, Store, CreditCard, Settings, LogOut, Menu, X,
  Activity, Users, MessageSquare
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Theme colors — exported for dashboard
const COLORS = {
  bg: "#F7F8FA",
  sidebar: "#0F0F0F",
  card: "#FFFFFF",
  border: "#F0F0F0",
  text: "#111111",
  textSecondary: "#6B7280",
  accent: "#FF4500",
  positive: "#22C55E",
  negative: "#EF4444",
  warning: "#F59E0B",
};

const navItems = [
  { path: "/super-admin/dashboard", icon: LayoutGrid, label: "Vue globale" },
  { path: "/super-admin/boutiques", icon: Store, label: "Boutiques", badgeKey: "boutiques" },
  { path: "/super-admin/payments", icon: CreditCard, label: "Paiements" },
  { path: "/super-admin/activity", icon: Activity, label: "Activité" },
  { path: "/super-admin/users", icon: Users, label: "Utilisateurs" },
  { path: "/super-admin/settings", icon: Settings, label: "Paramètres" },
  { path: "/super-admin/messages", icon: MessageSquare, label: "Messages", badgeKey: "messages" },
];

export default function SuperAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [adminSession, setAdminSession] = useState(null);
  const [pendingValidationCount, setPendingValidationCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    const session = sessionStorage.getItem("superAdminSession");
    if (!session) { navigate("/super-admin"); return; }
    setAdminSession(JSON.parse(session));

    // Fetch real badge counts
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${API}/admin/boutiques`);
        const pending = res.data.filter(b => b.compteValide !== true).length;
        setPendingValidationCount(pending);
      } catch {}
    };
    fetchCounts();
  }, [navigate]);

  const handleLogout = () => setShowLogoutDialog(true);
  const confirmLogout = () => { sessionStorage.removeItem("superAdminSession"); navigate("/super-admin"); };

  if (!adminSession) return null;

  const getBadge = (item) => {
    if (item.badgeKey === "boutiques" && pendingValidationCount > 0) return pendingValidationCount;
    if (item.badgeKey === "messages" && unreadMessages > 0) return unreadMessages;
    return 0;
  };

  const SidebarContent = ({ onNavClick }) => (
    <>
      {/* Brand */}
      <div style={{ padding: "24px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "rgba(255,69,0,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF4500" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <div>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "#FFFFFF", letterSpacing: -0.3 }}>BoutiqueOS</span>
            <div style={{ marginTop: 3 }}>
              <span style={{
                display: "inline-block", padding: "2px 7px", borderRadius: 4,
                background: COLORS.accent, color: "#fff",
                fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase",
              }}>SUPER ADMIN</span>
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 2 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const badge = getBadge(item);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavClick}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", borderRadius: 10,
                background: isActive ? "rgba(255,69,0,0.12)" : "transparent",
                color: isActive ? "#FF4500" : "rgba(255,255,255,0.5)",
                textDecoration: "none", transition: "all 150ms",
                fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: isActive ? 600 : 500,
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            >
              <item.icon size={18} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {badge > 0 && (
                <span style={{
                  minWidth: 20, height: 20, borderRadius: 10,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "#EF4444", color: "#fff", fontSize: 10, fontWeight: 700, padding: "0 5px",
                }}>{badge}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer — SA info */}
      <div style={{ padding: "16px 16px 20px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", marginBottom: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: COLORS.accent, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, fontFamily: "Inter, sans-serif", flexShrink: 0,
          }}>SA</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#FFFFFF", fontFamily: "Inter, sans-serif" }}>Super Admin</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {adminSession.email}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="superadmin-logout"
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "9px 12px", borderRadius: 8, border: "none", cursor: "pointer",
            background: "rgba(239,68,68,0.08)", color: "#EF4444",
            fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif",
          }}
        >
          <LogOut size={14} /> Se déconnecter
        </button>
      </div>
    </>
  );

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, fontFamily: "Inter, sans-serif" }}>
      {/* Desktop Sidebar */}
      <aside style={{
        position: "fixed", left: 0, top: 0, bottom: 0, width: 240,
        background: COLORS.sidebar, display: "flex", flexDirection: "column",
        zIndex: 40, borderRight: "1px solid rgba(255,255,255,0.04)",
      }} className="hidden md:flex">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden" style={{
        position: "fixed", top: 0, left: 0, right: 0, height: 56,
        background: COLORS.sidebar, zIndex: 40,
        display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,69,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF4500" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>BoutiqueOS</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 4 }}>
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 41 }} className="md:hidden" />
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.2 }}
              className="md:hidden"
              style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: 260, background: COLORS.sidebar, zIndex: 42, display: "flex", flexDirection: "column" }}>
              <SidebarContent onNavClick={() => setMobileMenuOpen(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main style={{ marginLeft: 0, paddingTop: 56, minHeight: "100vh" }} className="md:!ml-[240px] md:!pt-0">
        <div style={{ padding: "28px 32px", maxWidth: 1280, margin: "0 auto" }}>
          <Outlet />
        </div>
      </main>

      {/* Logout Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent style={{ background: "#fff", border: "1px solid #E5E7EB" }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: "#111" }}>Déconnexion Super Admin</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "#6B7280" }}>
              Voulez-vous vraiment vous déconnecter du tableau de bord administrateur ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-none" style={{ background: "#F3F4F6", color: "#111" }}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLogout} style={{ background: "#EF4444", color: "#fff" }}>Se déconnecter</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { COLORS };
